<template>
  <div class="container-fluid">
    <Toolbar class="m-2">
      <template #start>
        <h2>{{ $t("nav.user") }}</h2>
      </template>
      <template #end>
        <Button
          v-if="isAdmin"
          :label="$t('userpage.createUser')"
          icon="pi pi-plus"
          class="p-button-success mr-2 p-button-outlined p-button-rounded"
          @click="toUserForm()"
        />
      </template>
    </Toolbar>

    <Paginator
      v-model:first="actualPage"
      v-model:rows="pageSize"
      :total-records="totalItemsCount"
      @page="onPageChange($event)"
    />

    <DataTable
      v-model:sortField="sortField"
      v-model:sortOrder="sortOrder"
      v-model:filters="filters"
      striped-rows
      row-hover
      lazy
      :value="users"
      responsive-layout="scroll"
      filter-display="row"
      class="p-datatable-sm"
      @sort="onSort($event)"
      @filter="onFilter($event)"
    >
      <Column
        :exportable="false"
        style="width: 200px; min-width: 200px;"
      >
        <template #body="{data}">
          <Button
            v-if="isAgent || isAdmin"
            icon="pi pi-eye"
            class="p-button-rounded p-button-secondary mx-1"
            :title="$t('userpage.title.detail')"
            @click="showUserDetail(data.id)"
          />

          <Button
            v-if="isAgent || isAdmin"
            icon="pi pi-pencil"
            class="p-button-rounded mx-1"
            :title="$t('action.crud.update')"
            @click="toUserForm(data.id)"
          />

          <Button
            v-if="
              isAdmin && (data.role === contactRole || data.role === agentRole)
            "
            icon="pi pi-trash"
            class="p-button-rounded p-button-danger mx-1"
            :title="$t('userpage.title.delete')"
            @click="openDeleteDialog(data.id)"
          />

          <Button
            v-if="!data.approved && !data.approvalDate && isApprover(data.stakeholder?.stakeholderId) && !isAutoApproval"
            icon="pi pi-check-circle"
            class="p-button-rounded mx-1"
            :title="$t('userpage.title.approve')"
            @click="openApprovalDialog(data.id, true)"
          />

          <Button
            v-if="!data.approved && !data.approvalDate && isApprover(data.stakeholder?.stakeholderId) && !isAutoApproval"
            icon="pi pi-times-circle"
            class="p-button-danger p-button-rounded mx-1"
            :title="$t('userpage.title.reject')"
            @click="openRejectDialog(data.id, false)"
          />
        </template>
      </Column>
      <Column
        field="firstname"
        :header="$t('entity.user.firstname')"
        :sortable="true"
        :show-filter-menu="false"
        class="col-tab"
      >
        <template #body="{ data }">
          {{ data.firstname }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-model="filterModel.value"
            type="text"
            class="p-column-filter"
            @keydown.enter="filterCallback()"
          />
        </template>
      </Column>

      <Column
        field="lastname"
        :header="$t('entity.user.lastname')"
        :sortable="true"
        :show-filter-menu="false"
        class="col-tab"
      >
        <template #body="{ data }">
          {{ data.lastname }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-model="filterModel.value"
            type="text"
            class="p-column-filter"
            @keydown.enter="filterCallback()"
          />
        </template>
      </Column>

      <Column
        field="email"
        :header="$t('entity.user.email')"
        :sortable="true"
        :show-filter-menu="false"
        class="col-tab break"
      >
        <template #body="{ data }">
          {{ data.email }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-model="filterModel.value"
            type="text"
            class="p-column-filter"
            @keydown.enter="filterCallback()"
          />
        </template>
      </Column>

      <Column
        field="stakeholderId"
        :header="$t('entity.user.stakeholderId')"
        :sortable="true"
        :show-filter-menu="false"
        class="col-tab"
      >
        <template #body="{ data }">
          {{ data.stakeholder?.stakeholderId }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-model="filterModel.value"
            type="text"
            class="p-column-filter"
            @keydown.enter="filterCallback()"
          />
        </template>
      </Column>

      <Column
        field="stakeholderName"
        :header="$t('entity.stakeholder.name')"
        :sortable="true"
        :show-filter-menu="false"
        class="col-tab"
      >
        <template #body="{ data }">
          {{ data.stakeholder?.name }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-model="filterModel.value"
            type="text"
            class="p-column-filter"
            @keydown.enter="filterCallback()"
          />
        </template>
      </Column>

      <Column
        field="activated"
        :header="$t('entity.user.activated')"
        :sortable="false"
        :show-filter-menu="false"
        class="col-tab"
      >
        <template #body="{ data }">
          <em
            v-if="data.activated"
            class="pi pi-check"
          />
          <em
            v-else
            class="pi pi-times"
          />
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <TriStateCheckbox
            v-model="filterModel.value"
            @change="filterCallback()"
          />
        </template>
      </Column>
      <Column
        v-if="isAdmin"
        :header="$t('userpage.locked')"
        class="col-tab"
      >
        <template #body="{ data }">
          <div class="p-component p-checkbox-box">
            <Checkbox
              v-if="data.role === contactRole || data.role === agentRole"
              v-model="data.locked"
              :binary="true"
              @click.prevent="
                data.locked
                  ? openActivateDialog(data.id)
                  : openDeactivateDialog(data.id)
              "
            />
          </div>
        </template>
      </Column>

      <Column
        v-if="!isAutoApproval"
        :header="$t('entity.user.approved')"
        class="col-tab"
        :sortable="false"
        :show-filter-menu="false"
        field="approved"
      >
        <template #filter="{ filterModel, filterCallback }">
          <Dropdown
            v-model="filterModel.value"
            :options="userApprovedFilters"
            :option-label="(option) => $t(option.name)"
            option-value="value"
            :placeholder="$t('action.filter.all')"
            @change="filterCallback()"
          />
        </template>

        <template #body="{ data }">
          <em
            v-if="data.approved"
            class="pi pi-check"
          />
          <em
            v-if="data.approvalDate && !data.approved"
            class="pi pi-times"
          />
        </template>
      </Column>
      <Column
        v-if="isAdmin"
        :header="$t('entity.user.role')"
        class="col-tab"
        :sortable="false"
        :show-filter-menu="false"
        field="role"
      >
        <template #filter="{ filterModel, filterCallback }">
          <Dropdown
            v-model="filterModel.value"
            :options="userRoleFilters"
            :option-label="(option) => $t(option.name)"
            option-value="value"
            :placeholder="$t('action.filter.all')"
            @change="filterCallback()"
          />
        </template>

        <template #body="{ data }">
          <span>
            {{ $t(`entity.user.roles.${data.role}`) }}
          </span>
        </template>
      </Column>
    </DataTable>

    <Paginator
      v-model:first="actualPage"
      v-model:rows="pageSize"
      :total-records="totalItemsCount"
      @page="onPageChange($event)"
    />


    <Dialog
      v-model:visible="dialogDelete"
      :modal="true"
    >
      <template #header>
        <h3>{{ $t('userpage.dialog.delete.header') }}</h3>
      </template>

      <div class="confirmation-content">
        <span>{{ $t("userpage.dialog.delete.content") }}</span>
      </div>
      <template #footer>
        <Button
          :label="$t('userpage.dialog.abortButton')"
          icon="pi pi-times"
          class="p-button-secondary p-button-rounded p-button-outlined"
          @click="dialogDelete = false"
        />
        <Button
          :label="$t('userpage.dialog.confirmButton')"
          icon="pi pi-check"
          class="p-button-success mr-2 p-button-outlined p-button-rounded"
          @click="deleteUser"
        />
      </template>
    </Dialog>

    <Dialog
      v-model:visible="dialogActivate"
      :modal="true"
      @after-hide="refresh"
    >
      <template #header>
        <h3>{{ $t('userpage.dialog.activate.header') }}</h3>
      </template>


      <div class="confirmation-content">
        <span>{{ $t("userpage.dialog.activate.content") }}</span>
      </div>
      <template #footer>
        <Button
          :label="$t('userpage.dialog.abortButton')"
          icon="pi pi-times"
          class="p-button-secondary p-button-rounded p-button-outlined"
          @click="abort"
        />
        <Button
          :label="$t('userpage.dialog.confirmButton')"
          icon="pi pi-check"
          class="p-button-success mr-2 p-button-outlined p-button-rounded"
          @click="activateUser"
        />
      </template>
    </Dialog>

    <Dialog
      v-model:visible="dialogDeactivate"
      :modal="true"
      @after-hide="refresh"
    >
      <template #header>
        <h3>{{ $t('userpage.dialog.deactivate.header') }}</h3>
      </template>

      <div class="confirmation-content">
        <span>{{ $t("userpage.dialog.deactivate.content") }}</span>
      </div>
      <template #footer>
        <Button
          :label="$t('userpage.dialog.abortButton')"
          icon="pi pi-times"
          class="p-button-secondary p-button-rounded p-button-outlined"
          @click="abort"
        />
        <Button
          :label="$t('userpage.dialog.confirmButton')"
          icon="pi pi-check"
          class="p-button-success mr-2 p-button-outlined p-button-rounded"
          @click="deactivateUser"
        />
      </template>
    </Dialog>

    <Dialog
      v-model:visible="dialogReject"
      class="col-12 md:col-6"
      :modal="true"
    >
      <template #header>
        <h3>{{ $t('userpage.dialog.reject.header') }}</h3>
      </template>

      <div class="confirmation-content flex flex-column">
        <span>{{ $t("userpage.dialog.reject.content") }}</span>
        <span
          for="message"
          class="mt-3"
        >{{ $t("userpage.dialog.reject.message") }}</span>
        <Textarea
          id="message"
          v-model="rejectMessage"
          class="mt-2 col-12"
        />
      </div>
      <template #footer>
        <Button
          :label="$t('userpage.dialog.abortButton')"
          icon="pi pi-times"
          class="p-button-secondary p-button-rounded p-button-outlined"
          @click="dialogReject = false"
        />
        <Button
          :label="$t('userpage.dialog.confirmButton')"
          icon="pi pi-check"
          class="p-button-success mr-2 p-button-outlined p-button-rounded"
          @click="approveUser"
        />
      </template>
    </Dialog>

    <Dialog
      v-model:visible="dialogApproval"
      class="col-12 md:col-6"
      :modal="true"
    >
      <template #header>
        <h3>{{ $t('userpage.dialog.approval.header') }}</h3>
      </template>

      <div class="confirmation-content">
        <span>{{ $t("userpage.dialog.approval.content") }}</span>
      </div>
      <template #footer>
        <Button
          :label="$t('userpage.dialog.abortButton')"
          icon="pi pi-times"
          class="p-button-secondary p-button-rounded p-button-outlined"
          @click="dialogApproval = false"
        />
        <Button
          :label="$t('userpage.dialog.confirmButton')"
          icon="pi pi-check"
          class="p-button-success mr-2 p-button-outlined p-button-rounded"
          @click="approveUser"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>

import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Paginator from "primevue/paginator";
import InputText from "primevue/inputtext";
import Dialog from "primevue/dialog";
import Textarea from "primevue/textarea";
import Button from "primevue/button";
import {FilterMatchMode} from "primevue/api";
import Toolbar from "primevue/toolbar";
import {ADMIN_ROLE, AGENT_ROLE, CONTACT_ROLE} from "@/shared/constants";
import TriStateCheckbox from "primevue/tristatecheckbox";
import Checkbox from 'primevue/checkbox';
import Dropdown from "primevue/dropdown";

export default {
  name: "UserTable",
  components: {
    Column,
    DataTable,
    Paginator,
    InputText,
    Toolbar,
    Button,
    Dialog,
    TriStateCheckbox,
    Checkbox,
    Textarea,
    Dropdown
  },
  data() {
    return {
      users: [],
      page: 0,
      pageSize: 100,
      totalItemsCount: 0,
      sortField: "id",
      sortOrder: -1,
      filters: {
        firstname: {value: null, matchMode: FilterMatchMode.CONTAINS},
        lastname: {value: null, matchMode: FilterMatchMode.CONTAINS},
        email: {value: null, matchMode: FilterMatchMode.CONTAINS},
        stakeholderId: {value: null, matchMode: FilterMatchMode.CONTAINS},
        stakeholderName: {value: null, matchMode: FilterMatchMode.CONTAINS},
        activated: {value: null, matchMode: FilterMatchMode.EQUALS},
        approved: {value: null},
        role: {value: null},
      },
      userApprovedFilters: [
        {name: 'action.filter.all', value: null},
        {name: 'entity.user.approvefilter.approved', value: 'APPROVED'},
        {name: 'entity.user.approvefilter.notapproved', value: 'NOT_APPROVED'},
        {name: 'entity.user.approvefilter.toapprove', value: 'TO_APPROVE'},
      ],
      userRoleFilters: [
      {name: 'action.filter.all', value: null},
      {name: 'entity.user.roles.contact', value: CONTACT_ROLE},
      {name: 'entity.user.roles.agent', value: AGENT_ROLE},
      {name: 'entity.user.roles.admin', value: ADMIN_ROLE},
    ],
      isLoading: false,
      selectedId: null,
      dialogDelete: false,
      dialogActivate: false,
      dialogDeactivate: false,
      dialogReject: false,
      dialogApproval: false,
      checked: true,
      approved: false,
      rejectMessage: null,
      actualPage: 1,
    };
  },
  computed: {
    contactRole() {
      return CONTACT_ROLE;
    },
    agentRole() {
      return AGENT_ROLE;
    },
    isAgent() {
      return (
        this.$store.state.auth.user &&
        this.$store.state.auth.user.role === AGENT_ROLE
      );
    },
    isAdmin() {
      return this.$store.getters["auth/userIsAdmin"];
    },
    isAutoApproval() {
      return this.$store.getters['setting/isAutoApproval'];
    },
    currentUser() {
      return this.$store.state.auth.user;
    },
  },
  mounted() {
    if (this.currentUser.role === CONTACT_ROLE && !this.currentUser.approver) {
      this.$router.push('/')
    }
    this.refresh();
  },
  methods: {
    refresh() {
      this.getUsers();
    },
    async getUsers() {
      this.isLoading = true;
      try {
        const {content, totalElements} =
          await this.$userService.fetchUsersPagingAndSorting(
            this.getRequestParams()
          );
        this.users = content;
        this.totalItemsCount = totalElements;
      } catch (e) {
        this.$emitter.emit("toast", {
          severity: "error",
          message: "error.global",
        });
      } finally {
        this.isLoading = false;
      }
    },
    onPageChange(event) {
      this.page = event.page;
      this.getUsers();
      this.actualPage = event.page * this.lineIdFilter.pageable.size;
    },
    onSort(event) {
      this.sortField = event.sortField;
      this.sortOrder = event.sortOrder;
      this.getUsers();
    },
    onFilter(event) {
      this.filters = event.filters;
      this.getUsers();
    },
    getRequestParams() {
      let params = {};
      params["sort"] =
        this.sortField + "," + (this.sortOrder === 1 ? "asc" : "desc");
      if (this.page !== 0) {
        params["page"] = this.page;
      }
      if (this.pageSize) {
        params["size"] = this.pageSize;
      }
      if (this.filters.lastname.value) {
        params["lastname"] = this.filters.lastname.value;
      }
      if (this.filters.firstname.value) {
        params["firstname"] = this.filters.firstname.value;
      }
      if (this.filters.email.value) {
        params["email"] = this.filters.email.value;
      }
      if (this.filters.stakeholderId.value) {
        params["stakeholderId"] = this.filters.stakeholderId.value;
      }
      if (this.filters.stakeholderName.value) {
        params["stakeholderName"] = this.filters.stakeholderName.value;
      }
      if (this.filters.activated.value != null) {
        params["activated"] = this.filters.activated.value;
      }
      if (this.filters.approved.value != null) {
        params["approved"] = this.filters.approved.value;
      }
      if (this.filters.role.value != null) {
        params["role"] = this.filters.role.value;
      }
      return params;
    },
    toUserForm(userId) {
      if (userId) {
        this.$router.push({name: "userUpdate", params: {id: userId}});
      } else {
        this.$router.push({name: "userCreate"});
      }
    },
    openDeleteDialog(id) {
      this.selectedId = id;
      this.dialogDelete = true;
    },
    async deleteUser() {
      this.dialogDelete = false;
      try {
        const del = await this.$userService.deleteUser(this.selectedId);
        if (del) {
          this.$emitter.emit("toast", {
            severity: "success",
            message: "userpage.toast.delete.success",
          });
        } else {
          this.$emitter.emit("toast", {
            severity: "error",
            message: "userpage.toast.delete.error",
          });
        }
      } catch (e) {
        const errors = e.response.data.map(error => this.$t(error)).join(', ')
        this.$emitter.emit("toast", {
          severity: "error",
          message: errors,
        });
      }
      this.refresh();
    },
    openActivateDialog(id) {
      this.selectedId = id;
      this.dialogActivate = true;
    },
    openDeactivateDialog(id) {
      this.selectedId = id;
      this.dialogDeactivate = true;
    },
    openRejectDialog(id, approved) {
      this.selectedId = id;
      this.dialogReject = true;
      this.rejectMessage = null;
      this.approved = approved;
    },
    openApprovalDialog(id, approved) {
      this.selectedId = id;
      this.dialogApproval = true;
      this.approved = approved;
    },
    abort() {
      this.dialogActivate = false;
      this.dialogDeactivate = false;
      this.selectedId = null;
      this.refresh();
    },
    async activateUser() {
      this.dialogActivate = false;
      const activate = await this.$userService.unlockUserByAdmin(
        this.selectedId
      );
      if (activate) {
        this.$emitter.emit("toast", {
          severity: "success",
          message: "userpage.toast.activate.success",
        });
      } else {
        this.$emitter.emit("toast", {
          severity: "error",
          message: "userpage.toast.activate.error",
        });
      }
      this.refresh();
    },
    async deactivateUser() {
      this.dialogDeactivate = false;
      const deactivate = await this.$userService.lockUser(this.selectedId);
      if (deactivate) {
        this.$emitter.emit("toast", {
          severity: "success",
          message: "userpage.toast.deactivate.success",
        });
      } else {
        this.$emitter.emit("toast", {
          severity: "error",
          message: "userpage.toast.deactivate.error",
        });
      }
      this.refresh();
    },
    async approveUser() {
      this.dialogApproval = false;
      this.dialogReject = false;
      const userApproval = {
        id: this.selectedId,
        approved: this.approved,
        rejectMessage: this.rejectMessage
      }
      try {
        const approved = await this.$userService.approveUser(userApproval);
        if (approved) {
          this.$emitter.emit("toast", {
            severity: "success",
            message: "userpage.toast.approval.success",
          });
        } else {
          this.$emitter.emit("toast", {
            severity: "success",
            message: "userpage.toast.approval.reject",
          });
        }
        this.refresh();
      } catch (e) {
        const errors = e.response.data.map(error => this.$t(error)).join(', ')
        this.$emitter.emit("toast", {
          severity: "error",
          message: errors,
        });
      }
    },
    isApprover(stakeholderId) {
      return this.isAdmin ||
        (this.currentUser.approver &&
          (
            this.currentUser.stakeholdersApprover?.length === 0 ||
            this.currentUser.stakeholdersApprover?.some(stakeholder => stakeholder.stakeholderId === stakeholderId)
          )
        );
    },
    showUserDetail(id) {
      this.$router.push({name: "userDetail", params: {id: id}});
    }
  },
};
</script>

<style lang="scss" scoped>
.item-liste {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: baseline;
}


.pi-check {
  color: $green;
}

.pi-times {
  color: $red;
}

.p-button-ticket {
  background: $pink !important;
  border: 3px solid $pink !important;
}

</style>
